import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import ReactToPrint from "react-to-print";
import { useAppDispatch } from "../../../_App/Redux/hooks";
import { getOrderByIdApi } from "../../../_App/Redux/Slices/Orders/OrderSlice";
import { findPaymentsByTicketApi } from "../../../_App/Redux/Slices/payments/PaymentSlice";
import {
  findTicketsByOrderApi,
  getTicketById,
} from "../../../_App/Redux/Slices/tickets/TicketSlice";
import { useTranslation } from "react-i18next";
import Loader from "../../../_Layouts/Loader/Loader";
import { formatDateAndTime } from "../../../_App/Helpers/helpers";

type ReceiptData = {
  key: string;
  items: any[];
  totalNet: number;
  order_number?: string;
  date?: string;
  status?: string;
  payments: any[];
};

const computeTotals = (items: any[]) => {
  if (!Array.isArray(items)) {
    return { totalHT: 0, totalTTC: 0, totalRemise: 0 };
  }
  return items.reduce(
    (acc: any, item: any) => {
      const qty = Number(item.quantity) || 0;
      const priceHT = Number(item.article?.prix_vente_ht) || 0;
      const priceTTC = Number(item.article?.prix_vente_ttc) || 0;
      const remise = Number(item.remise) || 0;
      const lineHT = priceHT * qty;
      const lineTTC = priceTTC * qty;
      return {
        totalHT: acc.totalHT + lineHT,
        totalTTC: acc.totalTTC + lineTTC,
        totalRemise: acc.totalRemise + (lineTTC * remise) / 100,
      };
    },
    { totalHT: 0, totalTTC: 0, totalRemise: 0 },
  );
};

const OrderDetailsModal = ({ show, setShow, order_id, ticket_id }: any) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [order, setOrder] = useState<any>(null);
  const [receipts, setReceipts] = useState<ReceiptData[]>([]);
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetch = async () => {
      if (!show || (!order_id && !ticket_id)) return;
      setLoading(true);
      try {
        if (ticket_id) {
          // ticket_id is a stable identifier (unlike order_id, which can be
          // cleared from the cart/redux state right after payment), so it's
          // the safest source of truth when both are provided.
          const ticketRes: any = await dispatch(
            getTicketById({ id: ticket_id, order: undefined }),
          );
          const ticketOrder = ticketRes.payload?.ticket_order;
          if (ticketOrder) {
            setOrder(ticketOrder);
            const paymentsRes: any = await dispatch(
              findPaymentsByTicketApi(ticket_id),
            );
            setReceipts([
              {
                key: ticket_id,
                items: ticketOrder.items || [],
                totalNet: ticketOrder.totalNet ?? ticketOrder.total ?? 0,
                order_number: ticketOrder.order_number,
                date: ticketOrder.date,
                status: ticketOrder.status,
                payments: Array.isArray(paymentsRes.payload)
                  ? paymentsRes.payload
                  : [],
              },
            ]);
          } else {
            setOrder(null);
            setReceipts([]);
          }
        } else {
          const res = await dispatch(getOrderByIdApi(order_id));
          const baseOrder = res.payload && res.payload._id ? res.payload : null;
          setOrder(baseOrder);

          const ticketsRes: any = await dispatch(
            findTicketsByOrderApi(order_id),
          );
          const tickets = Array.isArray(ticketsRes.payload)
            ? ticketsRes.payload
            : [];

          if (tickets.length > 1) {
            // The order was paid across several tickets (partial payments,
            // split returns...): show each ticket as its own receipt.
            const paymentsPerTicket = await Promise.all(
              tickets.map((tk: any) =>
                dispatch(findPaymentsByTicketApi(tk._id)),
              ),
            );
            setReceipts(
              tickets.map((tk: any, index: number) => ({
                key: tk._id,
                items: tk.ticket_order?.items || [],
                totalNet: tk.ticket_order?.total ?? 0,
                order_number: tk.ticket_order?.order_number,
                date: tk.ticket_order?.date,
                status: tk.ticket_order?.status,
                payments: Array.isArray(paymentsPerTicket[index]?.payload)
                  ? paymentsPerTicket[index].payload
                  : [],
              })),
            );
          } else {
            const paymentsPerTicket = await Promise.all(
              tickets.map((tk: any) =>
                dispatch(findPaymentsByTicketApi(tk._id)),
              ),
            );
            const allPayments = paymentsPerTicket
              .filter((r: any) => Array.isArray(r.payload))
              .flatMap((r: any) => r.payload);
            setReceipts(
              baseOrder
                ? [
                    {
                      key: order_id,
                      items: baseOrder.items || [],
                      totalNet: baseOrder.totalNet ?? 0,
                      order_number: baseOrder.order_number,
                      date: baseOrder.date,
                      status: baseOrder.status,
                      payments: allPayments,
                    },
                  ]
                : [],
            );
          }
        }
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [show, order_id, ticket_id, dispatch]);

  useEffect(() => {
    if (!show) {
      setOrder(null);
      setReceipts([]);
    }
  }, [show]);

  const dashedLine: React.CSSProperties = {
    borderBottom: "1px dashed #d8dce6",
    margin: "14px 0",
  };

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      centered
      contentClassName="bg-transparent border-0"
    >
      {loading ? (
        <Loader />
      ) : order && receipts.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "20px 0",
            gap: "16px",
          }}
        >
          <div
            ref={componentRef}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              width: "100%",
            }}
          >
            {receipts.map((receipt, idx) => {
              const totals = computeTotals(receipt.items);
              const paymentDetails = _.groupBy(
                receipt.payments,
                "payment_method",
              );
              const totalPaid = Object.values(paymentDetails || {}).reduce(
                (sum: number, list: any) =>
                  sum +
                  list.reduce(
                    (s: number, p: any) => s + (Number(p.amount) || 0),
                    0,
                  ),
                0,
              );
              const status = receipt.status ?? order.status;
              const statusIsNegative =
                status === "Avoir" || status === "Retour";

              return (
                <div
                  key={receipt.key || idx}
                  style={{
                    width: "100%",
                    maxWidth: "440px",
                    background: "#fff",
                    borderRadius: "14px",
                    boxShadow: "0 12px 30px rgba(0,0,0,0.18)",
                    padding: "30px 28px",
                    fontFamily: "'Courier New', Courier, monospace",
                    color: "#1f2430",
                    fontSize: "15px",
                  }}
                >
                  {receipts.length > 1 && (
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#5a5f6b",
                        marginBottom: "8px",
                      }}
                    >
                      {t("Ticket")} {idx + 1}/{receipts.length}
                    </div>
                  )}
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "22px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {order.societe_id?.nom}
                    </div>
                    {order.societe_id?.tel && (
                      <div style={{ fontSize: "14px", color: "#5a5f6b" }}>
                        {t("Tel")}: {order.societe_id.tel}
                      </div>
                    )}
                    <span
                      style={{
                        display: "inline-block",
                        marginTop: "10px",
                        padding: "4px 14px",
                        borderRadius: "20px",
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#fff",
                        background: statusIsNegative ? "#e7515a" : "#00ab55",
                      }}
                    >
                      {t(status)}
                    </span>
                  </div>

                  <div style={dashedLine} />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "14px",
                    }}
                  >
                    <div>
                      <div style={{ color: "#5a5f6b" }}>{t("N°")}</div>
                      <div style={{ fontWeight: 700 }}>
                        {receipt.order_number}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#5a5f6b" }}>{t("Date")}</div>
                      <div style={{ fontWeight: 700 }}>
                        {receipt.date
                          ? formatDateAndTime(receipt?.date)
                          : "N/A"}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "14px",
                      marginTop: "10px",
                    }}
                  >
                    <div>
                      <div style={{ color: "#5a5f6b" }}>{t("Client")}</div>
                      <div style={{ fontWeight: 700 }}>
                        {order.client_id?.nom ?? "N/A"}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ color: "#5a5f6b" }}>{t("Caissier")}</div>
                      <div style={{ fontWeight: 700 }}>
                        {(order.user_id?.nom || "") +
                          " " +
                          (order.user_id?.prenom || "")}
                      </div>
                    </div>
                  </div>

                  {order.vendeur && (
                    <div style={{ fontSize: "14px", marginTop: "10px" }}>
                      <div style={{ color: "#5a5f6b" }}>{t("Vendeur")}</div>
                      <div style={{ fontWeight: 700 }}>
                        {(order.vendeur?.nom || "") +
                          " " +
                          (order.vendeur?.prenom || "")}
                      </div>
                    </div>
                  )}

                  <div style={dashedLine} />

                  <div>
                    {receipt.items?.map((item: any, index: number) => (
                      <div key={index} style={{ marginBottom: "10px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "15px",
                          }}
                        >
                          <span>
                            {item.quantity}x {item.article?.titre?.fr}
                          </span>
                          <span style={{ fontWeight: 700 }}>
                            {(Number(item.prix_total) || 0).toFixed(3)}
                          </span>
                        </div>
                        {Number(item.remise) > 0 && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontSize: "13px",
                              color: "#5a5f6b",
                            }}
                          >
                            <span>{t("Remise")}</span>
                            <span>{Number(item.remise)}%</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div style={dashedLine} />

                  <div style={{ fontSize: "15px" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{t("Total HT")}</span>
                      <span>{totals.totalHT.toFixed(3)}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{t("Total TTC")}</span>
                      <span>{totals.totalTTC.toFixed(3)}</span>
                    </div>
                    {totals.totalRemise > 0 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          color: "#e7515a",
                        }}
                      >
                        <span>{t("Remise")}</span>
                        <span>-{totals.totalRemise.toFixed(3)}</span>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontWeight: 700,
                        fontSize: "19px",
                        marginTop: "10px",
                      }}
                    >
                      <span>{t("Total Net")}</span>
                      <span>{Number(receipt.totalNet ?? 0).toFixed(3)} TND</span>
                    </div>
                  </div>

                  {Object.keys(paymentDetails || {}).length > 0 && (
                    <>
                      <div style={dashedLine} />
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: 700,
                          marginBottom: "8px",
                          color: "#5a5f6b",
                        }}
                      >
                        {t("Réglement(s)")}
                      </div>
                      {Object.entries(paymentDetails || {}).map(
                        ([method, list]: any) => {
                          const rendu = list.reduce(
                            (s: number, p: any) => s + (Number(p.rendu) || 0),
                            0,
                          );
                          return (
                            <React.Fragment key={method}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  fontSize: "15px",
                                  marginBottom: "4px",
                                }}
                              >
                                <span>
                                  {method === "Cash" ? "Espéces" : method}
                                </span>
                                <span style={{ fontWeight: 700 }}>
                                  {list
                                    .reduce(
                                      (s: number, p: any) =>
                                        s + (Number(p.amount) || 0),
                                      0,
                                    )
                                    .toFixed(3)}{" "}
                                  TND
                                </span>
                              </div>
                              {(method === "Cash" || method === "Espéces") &&
                                rendu > 0 && (
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      fontSize: "14px",
                                      color: "#5a5f6b",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <span>{t("Rendu")}</span>
                                    <span>{rendu.toFixed(3)} TND</span>
                                  </div>
                                )}
                            </React.Fragment>
                          );
                        },
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "15px",
                          marginTop: "8px",
                          paddingTop: "8px",
                          borderTop: "1px solid #ebedf2",
                          fontWeight: 700,
                        }}
                      >
                        <span>{t("Total payé")}</span>
                        <span>{totalPaid.toFixed(3)} TND</span>
                      </div>
                    </>
                  )}

                  <div style={dashedLine} />
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "13px",
                      color: "#5a5f6b",
                      fontStyle: "italic",
                    }}
                  >
                    {t("Merci de votre visite, à bientôt !")}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
      {order && receipts.length > 0 && (
        <div
          className="row m-0"
          style={{ justifyContent: "center", paddingBottom: "24px" }}
        >
          <ReactToPrint
            trigger={() => (
              <button
                className="btn btn-primary"
                style={{
                  width: "60%",
                  borderRadius: "20px",
                  fontSize: "16px",
                  fontWeight: 600,
                  padding: "10px 0",
                }}
              >
                {t("Imprimer")}
              </button>
            )}
            content={() => componentRef.current}
          />
        </div>
      )}
    </Modal>
  );
};

export default OrderDetailsModal;
